import { Link, useLocation } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import ButtonAndLinks from '#app/components/typography/button-and-links'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'

const getPaginationRange = (currentPage: number, totalPages: number) => {
	const range: (number | string)[] = []
	const showRange = 1

	range.push(1)

	// Add previous pages
	for (let i = Math.max(2, currentPage - showRange); i < currentPage; i++) {
		range.push(i)
	}

	// Add current page
	if (currentPage > 1 && currentPage < totalPages) {
		range.push(currentPage)
	}

	// Add next pages
	for (
		let i = currentPage + 1;
		i <= Math.min(totalPages - 1, currentPage + showRange);
		i++
	) {
		range.push(i)
	}

	if (totalPages > currentPage + showRange + 1) {
		range.push('...')
	}

	// Always add the last page
	if (!range.includes(totalPages)) {
		range.push(totalPages)
	}

	return [...new Set(range)].sort((a: any, b: any) => a - b)
}

const Pagination = ({
	currentPage,
	totalPages,
	onNextPage,
	onPreviousPage,
}: {
	currentPage: number
	totalPages: number
	onNextPage: () => void
	onPreviousPage: () => void
}) => {
	const location = useLocation()
	const { t } = useTranslation('product_list_page')
	const searchParams = new URLSearchParams(location.search)
	const paginationRange = getPaginationRange(currentPage, totalPages)
	const { trackAmplitudeEvent } = useAmplitudeContext()
	return (
		<div className="mb-14 mt-[44px] flex flex-col items-center justify-center gap-4 md:mt-[84px]">
			{currentPage < totalPages && (
				<Button
					onClick={onNextPage}
					variant="secondary"
					className="px-28 py-4"
					data-testid="plp-load-more-button"
					onClickCapture={() =>
						trackAmplitudeEvent('plp next page button clicked')
					}
				>
					<ButtonAndLinks as="s" body={t('view_next_page')} />
				</Button>
			)}
			<div className="item-center flex items-center justify-center gap-4">
				{currentPage > 1 && (
					<Icon
						onClick={onPreviousPage}
						className="text-body-md cursor-pointer"
						onClickCapture={() =>
							trackAmplitudeEvent('plp previous arrow button clicked')
						}
						size="sm"
						name="chevron-left-arrow"
					/>
				)}

				<div className="flex space-x-2">
					{paginationRange.map((page, index) => {
						if (page === '...') {
							return (
								<span
									key={index}
									className="flex h-8 w-8  items-center justify-center rounded-md border-[1px] border-beige-50 p-4"
								>
									{page}
								</span>
							)
						}

						searchParams.set('p', page.toString())
						const newSearch = searchParams.toString()

						return (
							<Link
								key={index}
								to={`?${newSearch}`}
								className={`flex h-8 w-8 items-center justify-center rounded-md p-4 ${
									currentPage === page
										? 'bg-black-90 text-white'
										: 'border-[1px] border-beige-50 bg-white text-black-90 hover:bg-beige-70'
								}`}
								onClickCapture={() =>
									trackAmplitudeEvent('plp page button clicked')
								}
							>
								{page}
							</Link>
						)
					})}
				</div>
				{currentPage < totalPages && (
					<Icon
						onClick={onNextPage}
						className="text-body-md cursor-pointer"
						onClickCapture={() =>
							trackAmplitudeEvent('plp next arrow button clicked')
						}
						size="sm"
						name="chevron-right-arrow"
					/>
				)}
			</div>
		</div>
	)
}

export default Pagination
